import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentUser } from '@/hooks/auth';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useToast } from '@/hooks/use-toast';

interface QuickPrompt {
  id: string;
  label: string;
  prompt: string;
}

const QUICK_PROMPTS_REPLY: QuickPrompt[] = [
  {
    id: 'availability',
    label: 'Confirmar disponibilidade',
    prompt: 'Confirme disponibilidade para uma reunião e peça horários disponíveis.',
  },
  {
    id: 'schedule-meeting',
    label: 'Agendar reunião',
    prompt: 'Proponha o agendamento de uma reunião para apresentar nossa solução.',
  },
  {
    id: 'more-info',
    label: 'Solicitar informações',
    prompt: 'Solicite mais informações sobre as necessidades do cliente de forma amigável.',
  },
  {
    id: 'follow-up',
    label: 'Follow-up',
    prompt: 'Faça um follow-up amigável sobre o interesse no produto/serviço.',
  },
  {
    id: 'decline',
    label: 'Declinar educadamente',
    prompt: 'Decline esta solicitação de forma educada e profissional.',
  },
];

const QUICK_PROMPTS_NEW: QuickPrompt[] = [
  {
    id: 'prospecting',
    label: 'Email de prospecção',
    prompt: 'Escreva um email de prospecção B2B apresentando brevemente nossa empresa e solução.',
  },
  {
    id: 'follow-up-meeting',
    label: 'Follow-up pós-reunião',
    prompt: 'Escreva um follow-up após uma reunião, resumindo os próximos passos combinados.',
  },
  {
    id: 'proposal',
    label: 'Enviar proposta',
    prompt: 'Escreva um email introduzindo o envio de uma proposta comercial.',
  },
  {
    id: 'feedback',
    label: 'Solicitar feedback',
    prompt: 'Solicite feedback do cliente sobre nossa proposta ou produto.',
  },
  {
    id: 'introduce',
    label: 'Apresentação da empresa',
    prompt: 'Escreva um email de apresentação da empresa para um potencial cliente.',
  },
];

interface GenerateOptions {
  prompt: string;
  contextHtml?: string;
  onSuccess: (html: string, subject?: string) => void;
}

export interface UseAiComposeReturn {
  aiModeActive: boolean;
  isGenerating: boolean;
  promptText: string;
  quickPromptsReply: QuickPrompt[];
  quickPromptsNew: QuickPrompt[];
  toggleAiMode: () => void;
  setPromptText: (text: string) => void;
  generate: (opts: GenerateOptions) => Promise<void>;
  dismissAiMode: () => void;
}

export function useAiCompose(): UseAiComposeReturn {
  const [aiModeActive, setAiModeActive] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [promptText, setPromptText] = useState('');

  const { user } = useCurrentUser();
  const { currentOrg, currentOrgId } = useOrganization();
  const { toast } = useToast();

  const toggleAiMode = () => setAiModeActive((prev) => !prev);

  const dismissAiMode = () => {
    setAiModeActive(false);
    setPromptText('');
  };

  const generate = async ({ prompt, contextHtml, onSuccess }: GenerateOptions) => {
    if (!prompt.trim()) {
      toast({
        title: 'Digite uma instrução',
        description: 'Descreva o que a IA deve escrever antes de gerar.',
        variant: 'destructive',
      });
      return;
    }

    if (!currentOrgId) {
      toast({
        title: 'Organização não encontrada',
        description: 'Selecione uma organização e tente novamente.',
        variant: 'destructive',
      });
      return;
    }

    if (!user) {
      toast({
        title: 'Usuário não autenticado',
        description: 'Faça login novamente e tente de novo.',
        variant: 'destructive',
      });
      return;
    }

    setIsGenerating(true);

    try {
      // Strip HTML from context to save tokens (keep only meaningful text)
      const plainContext = contextHtml
        ? contextHtml
            .replace(/<script[\s\S]*?<\/script>/gi, '')
            .replace(/<style[\s\S]*?<\/style>/gi, '')
            .replace(/<[^>]+>/g, ' ')
            .replace(/&amp;/g, '&')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .replace(/&nbsp;/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()
            .slice(0, 2000)
        : undefined;

      const { data, error } = await supabase.functions.invoke('ai-compose-email', {
        body: {
          org_id: currentOrgId,
          prompt,
          context: plainContext || undefined,
          sender_name: user?.full_name ?? undefined,
          sender_email: user?.email ?? undefined,
          org_name: currentOrg?.name ?? undefined,
        },
      });

      if (error) {
        // Supabase FunctionsHttpError wraps errors — extract the body for meaningful messages
        let errorMsg = '';
        try {
          if (typeof (error as any).context?.json === 'function') {
            const body = await (error as any).context.json();
            errorMsg = body?.error || body?.message || JSON.stringify(body);
          } else {
            errorMsg = (error as any).message || String(error);
          }
        } catch {
          errorMsg = (error as any).message || String(error);
        }
        console.error('useAiCompose: edge function error', { error, errorMsg });

        if (errorMsg.includes('Limite') || errorMsg.includes('limite') || errorMsg.includes('429')) {
          toast({
            title: 'Limite diário atingido',
            description: 'Você atingiu o limite de 50 gerações por dia. Tente novamente amanhã.',
            variant: 'destructive',
          });
        } else if (errorMsg.includes('não configurado') || errorMsg.includes('not set') || errorMsg.includes('ANTHROPIC_API_KEY')) {
          toast({
            title: 'Serviço de IA não configurado',
            description: 'A chave da API de IA não está configurada. Entre em contato com o suporte.',
            variant: 'destructive',
          });
        } else if (errorMsg.includes('Falha ao gerar') || errorMsg.includes('502')) {
          toast({
            title: 'Erro na geração',
            description: 'O serviço de IA está temporariamente indisponível. Tente novamente em alguns segundos.',
            variant: 'destructive',
          });
        } else if (errorMsg.includes('Edge Function') || errorMsg.includes('boot error') || errorMsg.includes('not found')) {
          toast({
            title: 'Função não disponível',
            description: 'O serviço de geração de IA precisa ser implantado. Entre em contato com o suporte.',
            variant: 'destructive',
          });
        } else {
          toast({
            title: 'Erro ao gerar email',
            description: errorMsg || 'Não foi possível conectar ao serviço de IA. Tente novamente.',
            variant: 'destructive',
          });
        }
        return;
      }

      if (data?.error) {
        if (data.error.includes('Limite') || data.error.includes('limite')) {
          toast({
            title: 'Limite diário atingido',
            description: `Você atingiu o limite de 50 gerações por dia. Tente novamente amanhã.`,
            variant: 'destructive',
          });
        } else {
          toast({
            title: 'Erro na geração',
            description: data.error,
            variant: 'destructive',
          });
        }
        return;
      }

      if (!data?.html) {
        console.error('useAiCompose: no html in response', { data });
        toast({
          title: 'Sem resultado',
          description: 'A IA não retornou conteúdo. Tente uma instrução diferente.',
          variant: 'destructive',
        });
        return;
      }

      onSuccess(data.html, data.subject || undefined);
      setPromptText('');
    } catch (err: unknown) {
      console.error('useAiCompose: unexpected error', err, { currentOrgId, hasUser: !!user });
      toast({
        title: 'Erro ao gerar email',
        description: 'Ocorreu um erro inesperado. Tente novamente.',
        variant: 'destructive',
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return {
    aiModeActive,
    isGenerating,
    promptText,
    quickPromptsReply: QUICK_PROMPTS_REPLY,
    quickPromptsNew: QUICK_PROMPTS_NEW,
    toggleAiMode,
    setPromptText,
    generate,
    dismissAiMode,
  };
}
